package com.arena_web.pages

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

// Figma 9954:143759 — desktop 404 screen. The "404" reads like a stylised
// scoreline: solid primary on the left, a primary→deep-blue gradient on the
// centre 0, and the deep-blue terminal on the right. #003A99 is unique to
// this screen (not part of the sport-color palette), so it lives as a local
// constant rather than getting bolted onto SportColorScheme.
private const val DEEP_BLUE = "#003A99"

@Page("/404")
@Composable
fun NotFoundPage() {
    val ctx = rememberPageContext()

    AppPageLayout(activeTab = null) {
        // Fill the remaining vertical space inside the layout's content slot
        // and centre both axes so the scoreboard + card pair sits roughly in
        // the middle regardless of viewport height.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .sPaddingTop(40)
                .sPaddingBottom(40),
            contentAlignment = Alignment.Center,
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Scoreboard()
                MessageCard(onBack = { ctx.router.navigateTo("/arena") })
            }
        }
    }
}

@Composable
private fun Scoreboard() {
    val sport = LocalSportColors.current
    val primaryHex = sport.primary.toCssHex()

    Row(
        modifier = Modifier.styleModifier {
            property("gap", s(12))
            property("line-height", "1")
        },
        verticalAlignment = Alignment.Top,
    ) {
        // Left "4" — solid sport primary.
        SpanText(
            "4",
            modifier = scoreboardDigitBase()
                .styleModifier {
                    property("color", primaryHex)
                    property("letter-spacing", s(13.2))
                    property("line-height", "1")
                },
        )
        // Centre "0" — horizontal gradient from primary to the deep-blue.
        // Rendered via `background-clip: text` so the gradient fills the glyph.
        SpanText(
            "0",
            modifier = scoreboardDigitBase().styleModifier {
                property(
                    "background",
                    "linear-gradient(90.018deg, $primaryHex 36.978%, $DEEP_BLUE 84.926%)",
                )
                property("-webkit-background-clip", "text")
                property("background-clip", "text")
                property("color", "transparent")
                property("letter-spacing", s(13.2))
                property("line-height", "1")
            },
        )
        // Right "4" — terminal deep-blue.
        SpanText(
            "4",
            modifier = scoreboardDigitBase()
                .color(Color(DEEP_BLUE))
                .styleModifier {
                    property("letter-spacing", s(13.2))
                    property("line-height", "1")
                },
        )
    }
}

private fun scoreboardDigitBase(): Modifier = Modifier
    .sFontSize(220)
    .fontWeight(AppTypography.SemiBold)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

@Composable
private fun MessageCard(onBack: () -> Unit) {
    val t = LocalAppStrings.current
    // The card overlaps the lower portion of the "404" so the two visually
    // interlock (Figma absolutes the card to top:171 inside a 220-tall
    // scoreboard, with a backdrop-blur so the digits behind the bottom of
    // the card read as slightly defocused — same blur recipe as
    // BottomNavBar's translucent surface).
    Box(
        modifier = Modifier
            .styleModifier {
                property("margin-top", "-${s(70)}")
                property("border-radius", s(24))
                property(
                    "background",
                    "linear-gradient(12.58deg, rgba(0, 59, 174, 0.16) 26.896%, rgba(3, 87, 230, 0.16) 67.539%)",
                )
                property("backdrop-filter", "blur(20px)")
                property("-webkit-backdrop-filter", "blur(20px)")
                property("position", "relative")
                property("z-index", "1")
            }
            .sPaddingTop(32)
            .sPaddingBottom(24)
            .sPaddingLeft(40)
            .sPaddingRight(40),
    ) {
        Column(
            modifier = Modifier.sGap(40),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier.sGap(12),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(
                    t.pageNotFoundTitle,
                    modifier = Modifier
                        .sFontSize(40)
                        .sLineHeight(60)
                        .fontWeight(AppTypography.Medium)
                        .color(AppColors.Text.Secondary)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        .styleModifier {
                            property("letter-spacing", s(2.4))
                            property("text-align", "center")
                            property("white-space", "nowrap")
                        },
                )
                Box(modifier = Modifier.sWidth(520)) {
                    SpanText(
                        t.pageNotFoundBody,
                        modifier = Modifier
                            .sFontSize(24)
                            .sLineHeight(34)
                            .fontWeight(AppTypography.SemiBold)
                            .color(Color("#99B1BF"))
                            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                            .styleModifier {
                                property("letter-spacing", s(0.48))
                                property("text-align", "center")
                            },
                    )
                }
            }
            BackToArenaButton(onClick = onBack)
        }
    }
}

@Composable
private fun BackToArenaButton(onClick: () -> Unit) {
    // 260×56 pill, primary-bg, with a 44px secondary-bg inner circle holding
    // the up-right arrow (ArrowUpOutline + 45° rotate matches the join/start
    // CTA pattern from MatchInfoBanner / ArenaEventCard).
    val sport = LocalSportColors.current
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sWidth(260)
            .sHeight(56)
            .sBorderRadius(100)
            .backgroundColor(sport.primary)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .sPaddingLeft(36)
            .sPaddingRight(6)
            .sPaddingTop(4)
            .sPaddingBottom(4),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            t.pageBackToArena,
            modifier = Modifier
                .flexGrow(1)
                .sFontSize(18)
                .sLineHeight(22)
                .fontWeight(AppTypography.Medium)
                .color(sport.onPrimary)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                .styleModifier {
                    property("letter-spacing", s(0.09))
                    property("text-align", "center")
                    property("transform", "translateY(1px)")
                },
        )
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .backgroundColor(sport.secondary),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                src = AppIcons.ArrowUpOutline,
                modifier = Modifier.styleModifier {
                    property("transform", "rotate(45deg)")
                    property("filter", sport.iconFilterOnSecondary)
                },
            )
        }
    }
}

/** Render a Kobweb [Color] as a `#RRGGBB` CSS string for embedding inside a
 *  raw style property (gradient stops, custom CSS expressions, etc.). */
private fun Color.toCssHex(): String {
    val rgb = toRgb()
    fun Int.hex2(): String = toString(16).padStart(2, '0')
    return "#${rgb.red.hex2()}${rgb.green.hex2()}${rgb.blue.hex2()}"
}
